import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, AppBar, Toolbar, Typography, Container, Button, Stack } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import CampaignListView from './components/CampaignListView';
import CampaignDetailView from './components/CampaignDetailView';
import NewCampaignForm from './components/NewCampaignForm';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import UserManagement from './components/UserManagement';
import { AuthService } from './services/auth';
import { User } from 'firebase/auth';

// Import logo
import logo from './ydlogo.png';

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb', // Modern blue
      light: '#60a5fa',
      dark: '#1e40af',
    },
    secondary: {
      main: '#db2777', // Modern pink
      light: '#f472b6',
      dark: '#be185d',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(145deg, #2563eb 0%, #1e40af 100%)',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const currentUser = AuthService.getCurrentUser();
    
    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    return <>{children}</>;
};

const AppContent: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const checkUserAndAdmin = async () => {
            const unsubscribe = AuthService.onAuthStateChanged(async (user) => {
                setUser(user);
                if (user) {
                    const adminStatus = await AuthService.isAdmin(user.uid);
                    setIsAdmin(adminStatus);
                } else {
                    setIsAdmin(false);
                }
                setLoading(false);
            });

            return () => unsubscribe();
        };

        checkUserAndAdmin();
    }, []);

    const handleLogout = async () => {
        try {
            await AuthService.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to log out:', error);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: '100vh',
            width: '100%',
            position: 'relative',
            isolation: 'isolate'
        }}>
            <AppBar position="static" elevation={0}>
                <Toolbar>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        flexGrow: 1 
                    }}>
                        <Box
                            component="img"
                            src={logo}
                            alt="CruiseClick Logo"
                            sx={{
                                height: 40,
                                mr: 2,
                                objectFit: 'contain'
                            }}
                        />
                        <Typography 
                            variant="h6" 
                            component="div" 
                            sx={{ 
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            CruiseClick
                        </Typography>
                    </Box>
                    {user && (
                        <Stack direction="row" spacing={2}>
                            {isAdmin && (
                                <Button
                                    color="inherit"
                                    onClick={() => navigate('/users')}
                                    startIcon={<PeopleIcon />}
                                    sx={{
                                        fontWeight: 500,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                        }
                                    }}
                                >
                                    Users
                                </Button>
                            )}
                            <Button
                                color="inherit"
                                onClick={handleLogout}
                                startIcon={<LogoutIcon />}
                                sx={{
                                    fontWeight: 500,
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                Logout
                            </Button>
                        </Stack>
                    )}
                </Toolbar>
            </AppBar>
            
            <Container 
                component="main" 
                sx={{ 
                    flexGrow: 1, 
                    py: 4,
                    px: { xs: 2, sm: 3, md: 4 }
                }}
            >
                <Routes>
                    <Route path="/login" element={
                        user ? <Navigate to="/" /> : <LoginPage />
                    } />
                    <Route path="/signup" element={
                        user ? <Navigate to="/" /> : <SignupPage />
                    } />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/" element={
                        <ProtectedRoute>
                            <CampaignListView />
                        </ProtectedRoute>
                    } />
                    <Route path="/campaign/new" element={
                        <ProtectedRoute>
                            <NewCampaignForm />
                        </ProtectedRoute>
                    } />
                    <Route path="/campaign/:id" element={
                        <ProtectedRoute>
                            <CampaignDetailView />
                        </ProtectedRoute>
                    } />
                    <Route path="/users" element={
                        <ProtectedRoute>
                            <UserManagement />
                        </ProtectedRoute>
                    } />
                </Routes>
            </Container>
            
            <Box 
                component="footer" 
                sx={{ 
                    py: 3, 
                    px: 2, 
                    mt: 'auto', 
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center'
                }}
            >
                <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                >
                    CruiseClick © {new Date().getFullYear()}
                </Typography>
            </Box>
        </Box>
    );
};

const App: React.FC = () => {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/*" element={<AppContent />} />
                </Routes>
            </Router>
        </ThemeProvider>
    );
};

export default App;
